import CookieService from '@/services/isomorphic/CookieService';

import { ServerPreviewService } from '@/services/serverless/ServerPreviewService';
import { isNullOrEmpty } from '@/utils/null-utils';
import Service from '../../Service';
import { EnvironmentService } from '../EnvironmentService';
import { RenderContextService } from '../RenderContextService';

export const PREVIEW_DATE_COOKIE_NAME = 'deckers_preview_date';

/** An isomorphic service for querying preview state. */
class PreviewService extends Service {
  /**
   * Is preview currently enabled. This might change any given
   * service to use a preview mode. Content being the most obvious one.
   * @returns - A boolean representing preview mode or not.
   */
  public get isPreviewActive(): boolean {
    return this.previewDate !== null;
  }

  /**
   * Gets the preview value from the correct source.
   * @returns - The preview value, or `null` if one isn't set.
   */
  private get previewValue(): string | null {
    if ((process.env.NEXT_PUBLIC_APP_ENV === "prod")) return null;
    if ((typeof window === "undefined")) {
      // When accessed during RSC rendering, we must retrieve the value from the render context.
      if (RenderContextService.isContextSupported)
        return RenderContextService.context.preview;
      // Perhaps we are in a route handler, in which case we can only
      // return the value if preview is available for the route.
      if (ServerPreviewService.isPreviewAvailable)
        return CookieService.tryGet(PREVIEW_DATE_COOKIE_NAME)?.value ?? null;
      // Otherwise we are in a server context which cannot access preview cookies, e.g. SSR.
      return null;
    }
    // On the client, we can always access the cookies.
    return CookieService.tryGet(PREVIEW_DATE_COOKIE_NAME)?.value ?? null;
  }

  /**
   * Gets the preview date.
   * @returns - The preview date on the Date object.
   */
  public get previewDate(): Date | null {
    const { previewValue } = this;
    if (isNullOrEmpty(previewValue)) return null;
    const msSinceEpoch = Number.parseInt(previewValue, 10);
    if (Number.isNaN(msSinceEpoch)) return null;
    return new Date(msSinceEpoch);
  }

  /**
   * Creates a URL string which can be shared to start a preview session.
   * @param location - The URL to start the preview session from.
   * @param dateInMs - The preview date in milliseconds since epoch.
   * @returns - The URL with the preview data added.
   */
  public getPreviewURL(location: URL, dateInMs: number | null): string {
    const url = new URL('/api/preview', EnvironmentService.url.origin);
    url.searchParams.set('slug', location.toString());
    if (dateInMs === null || Number.isNaN(dateInMs)) return url.toString();
    url.searchParams.set('previewDate', dateInMs.toString());
    return url.toString();
  }
}

export default new PreviewService();
